import { A, P } from '@osmosis/tailwind-ui/components/Text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Card } from '@osmosis/tailwind-ui/components/Card';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFetch } from '../../context/FetchProvider';
import Message from '../Message';

import { slideVertical } from '../../styles/animation';
import { AxiosResponse } from 'axios';

import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import InputValidation from '../InputValidation';
import { authMessages } from '../../constants/authLocalization';
import { usePagesContext } from '../../context/PagesProvider';
import { bg0, bg1, textBlue } from '@osmosis/tailwind-ui/styles';

const EMAIL = 'email';

const values = {
  a: (chunk: React.ReactNode) => (
    <A key="a" href="mailto:support@osmosis.org">
      {chunk}
    </A>
  ),
};

type Props = {
  onClose?: (email?: string) => void;
  redirectToLogin?: boolean;
};

type FormValues = { email: string };

/** Component to handle forgotten passwords. */
const ForgotPassword = (props: Props) => {
  const { onClose, redirectToLogin = true } = props;

  const [statusCode, setStatusCode] = useState<number | null>(null);
  const { post } = useFetch();

  const intl = useIntl();

  const schema = yup
    .object({
      [EMAIL]: yup
        .string()
        .email(intl.formatMessage(authMessages.invalidEmail))
        .required(intl.formatMessage(authMessages.emailRequired)),
    })
    .required();

  const {
    register,
    handleSubmit,
    getValues,
    setFocus,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  const { push } = usePagesContext();

  const handleBackToSignIn = () => {
    const email = getValues('email');

    if (redirectToLogin) {
      email && push(`/login?email=${email}`);
    }

    onClose && onClose(email);
  };

  const onSubmit: SubmitHandler<FormValues> = async ({ email }) => {
    const res = (await post('/forgotPassword', { email })) as AxiosResponse<{
      msg: string;
      statusCode: number;
    }>;

    res.data?.statusCode && setStatusCode(res.data?.statusCode);
  };

  const errorMessage = errors?.[EMAIL]?.message;

  useEffect(() => {
    setFocus(EMAIL);
  }, [setFocus]);

  return (
    <Card className={`border-none p-8 pt-0`}>
      {!statusCode && (
        <Box>
          <form>
            <Box className={`mb-4`}>
              <label htmlFor={'forgot-password-email-input'}>
                <FormattedMessage
                  id="login.forgot-password.email"
                  defaultMessage="Email"
                />
              </label>
              <InputValidation
                inputProps={{
                  ...register(EMAIL),
                  'data-testid': 'forgot-password-email-input',
                }}
                error={!!errorMessage}
                errorMessage={errorMessage as string}
              />
            </Box>
            <Button
              type="submit"
              size="l"
              onClick={handleSubmit(onSubmit)}
              data-testid="reset-password-btn"
              className={`w-full`}
            >
              <FormattedMessage
                id="login.forgot-password.reset-password"
                defaultMessage="Reset password"
              />
            </Button>
          </form>
        </Box>
      )}

      <AnimatePresence>
        {!!statusCode && (
          <motion.div {...slideVertical()}>
            <Box>
              <Message cn={`${bg1}`}>
                <P>
                  {statusCode === 200 && (
                    <FormattedMessage
                      {...authMessages.forgotPasswordMsgSuccess}
                      values={{ email: getValues('email') }}
                    />
                  )}

                  {statusCode === 403 && (
                    <FormattedMessage
                      {...authMessages.forgotPasswordMsgForbidden}
                      values={values}
                    />
                  )}

                  {statusCode === 429 && (
                    <FormattedMessage
                      {...authMessages.forgotPasswordMsgManyReq}
                      values={values}
                    />
                  )}
                </P>
              </Message>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
      <Box className={`flex justify-center`}>
        <Button
          onClick={handleBackToSignIn}
          color="transparent"
          data-testid="back-to-signin-btn"
          className={`text-base font-normal ${textBlue}dark:text-blue-100 mt-4 cursor-pointer ${bg0} hover:bg-transparent hover:text-blue-800`}
          type="button"
        >
          <FormattedMessage
            id="login.forgot-password.back"
            defaultMessage="Back to sign in"
          />
        </Button>
      </Box>
    </Card>
  );
};

export default ForgotPassword;
